import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { NextPage } from 'next';
import { useDispatch, useSelector } from 'react-redux';

import logoImg from '../assets/images/kecobo-logo.png';
import { withDashboardRedirect } from '../HOC/with-redirect';
import SignInForm from '../components/forms/auth/signin-form';
import backgroundImg from '../assets/images/onboarding-desktop.jpg';

import { RootState } from '../redux';
import { signin } from '../redux/auth/actions';
import { sendAnalytics } from '../redux/analytics/actions';
import { SigninInputs, SigninErrors } from '../redux/auth/interfaces';

const SigninPage: NextPage = () => {
  const dispatch = useDispatch();

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { isFetching, authToken, errors } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    setIsLoggedIn(authToken !== undefined);
  }, [authToken]);

  const loginUser = (data: SigninInputs) => {
    dispatch(signin(data));
  };

  useEffect(() => {
    dispatch(
      sendAnalytics({
        event: 'signinPage',
        category: 'Navigation',
        meta: {},
      }),
    );
  }, []);

  return (
    <div className="bg-white">
      <div className="flex h-full">
        <div className="flex items-center flex-1 px-6 lg:px-12">
          <div className="max-w-lg mx-auto my-10 text-gray-900 md:max-w-xl">
            <div className="w-40 mx-auto lg:w-48">
              <img src={logoImg} alt="" />
            </div>

            <p className="mt-10 text-3xl font-bold leading-tight tracking-wide text-center text-header sm:text-5xl lg:mt-12">
              Kenya National Rights Registry Portal
            </p>

            <p className="px-0 mt-6 text-sm font-normal text-justify lg:font-medium sm:px-0 sm:text-lg lg:mt-12">
              The National Rights Registry (NRR) portal is the central repository collating details
              pertaining to ownership of various copyright works. This portal is used by copyright
              holders allowing them to register and view/download copyright certificates. The portal
              allows rights holders of the following types of works: music, video, spoken word,
              literary works etc.
            </p>

            <div className="mt-10">
              <SignInForm
                loginUser={loginUser}
                isLoggedIn={isLoggedIn}
                isSigningIn={isFetching}
                errors={errors as SigninErrors}
              />

              <p className="py-2 pr-4 mt-2 text-sm font-medium text-right">
                Forgot password?
                <Link href="/reset-password">
                  <a className="text-secondary"> Recover or Reset.</a>
                </Link>
              </p>

              <p className="mt-4 font-medium text-center text-md">
                Don&apos;t have an account yet?
                <Link href="/">
                  <a className="text-secondary"> Sign Up.</a>
                </Link>
              </p>
            </div>
          </div>
        </div>

        <div
          className="flex-1 hidden bg-center bg-cover lg:block"
          style={{ backgroundImage: `url(${backgroundImg})` }}
        />
      </div>

      <div className="flex w-full px-6 py-4 border-t border-gray-400 lg:px-12">
        <p className="flex-1">
          For help contact,{' '}
          <span className="font-semibold ">
            <a href="tel: 0791399159" className="text-indigo-500">
              0791399159
            </a>
            ,{' '}
            <a href="tel: 0756460413" className="text-indigo-500">
              0756460413
            </a>
          </span>{' '}
          or send an email to{' '}
          <span className="font-semibold ">
            <a href="mailto: nrr@copyright.go.ke" className="text-indigo-500">
              nrr@copyright.go.ke
            </a>
          </span>
        </p>
        <p className="flex-0">
          Kenya Copyrights Board,{' '}
          <span className="font-semibold">&#169;{new Date().getFullYear()}</span>
        </p>
      </div>
    </div>
  );
};

export default withDashboardRedirect(SigninPage);
